/**
 * PiiCardScanner — Scan SSN cards, driver's licenses, insurance cards via camera
 *
 * Mobile-first component for capturing PII documents:
 * 1. User taps "Scan Card" → camera opens
 * 2. User takes photo of SSN card / DL / insurance card
 * 3. Photo sent to Presidio (local OCR + PII detection)
 * 4. User reviews extracted data before confirming
 * 5. On confirm, PII stored in vault, tokens returned
 *
 * DRR-302: Raw PII never goes to Claude or main FERP system.
 * Presidio runs in isolated service, vault is separate AWS account.
 *
 * Usage:
 *   <PiiCardScanner
 *     subjectId="person_001"
 *     onComplete={(result) => console.log('Tokens:', result.tokens)}
 *   />
 */

import React, { useState, useCallback } from 'react';
import {
  Card,
  Stack,
  Group,
  Text,
  Title,
  ThemeIcon,
  Button,
  Alert,
  Badge,
  Table,
  Loader,
  Paper,
} from '@mantine/core';
import {
  IconCreditCard,
  IconCamera,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconShieldCheck,
  IconRefresh,
} from '@tabler/icons-react';
import { LivePhotoCapture } from './LivePhotoCapture';
import { useImagePiiExtract, type ImagePiiResult, type ExtractedPiiField } from './hooks/useImagePiiExtract';

// Document type display names and icons
const DOCUMENT_TYPE_INFO: Record<string, { label: string; color: string }> = {
  SSN_CARD: { label: 'Social Security Card', color: 'blue' },
  DOCUMENT_WITH_SSN: { label: 'Document with SSN', color: 'blue' },
  DRIVERS_LICENSE: { label: "Driver's License", color: 'green' },
  DOCUMENT_WITH_DL: { label: "Document with Driver's License", color: 'green' },
  INSURANCE_CARD: { label: 'Insurance Card', color: 'violet' },
  PASSPORT: { label: 'Passport', color: 'orange' },
  DOCUMENT_WITH_PII: { label: 'Document with PII', color: 'gray' },
};

const ENTITY_TYPE_LABELS: Record<string, string> = {
  US_SSN: 'Social Security Number',
  US_DRIVER_LICENSE: "Driver's License Number",
  PHONE_NUMBER: 'Phone Number',
  EMAIL_ADDRESS: 'Email Address',
  DATE_OF_BIRTH: 'Date of Birth',
  ADDRESS: 'Address',
  PERSON: 'Name',
  CREDIT_CARD: 'Credit Card',
  US_PASSPORT: 'Passport Number',
};

type ScannerPhase = 'IDLE' | 'CAPTURING' | 'EXTRACTING' | 'REVIEW' | 'CONFIRMED' | 'ERROR';

export interface PiiCardScannerProps {
  subjectId?: string;
  documentId?: string;
  onComplete?: (result: { tokens: Record<string, string>; documentType: string | null }) => void;
  onCancel?: () => void;
  allowedTypes?: string[];  // Filter to specific document types
}

export const PiiCardScanner: React.FC<PiiCardScannerProps> = ({
  subjectId,
  documentId,
  onComplete,
  onCancel,
  allowedTypes,
}) => {
  const [phase, setPhase] = useState<ScannerPhase>('IDLE');
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [extractionResult, setExtractionResult] = useState<ImagePiiResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { extractPii, isExtracting } = useImagePiiExtract();

  const handlePhotoCapture = useCallback(async (blob: Blob) => {
    setCapturedBlob(blob);
    setPreviewUrl(URL.createObjectURL(blob));
    setPhase('EXTRACTING');

    try {
      const result = await extractPii(blob, {
        subjectId,
        documentId,
        storeInVault: false,  // Don't store yet — wait for user confirmation
      });

      // Check if document type is allowed (if filter specified)
      if (allowedTypes && result.documentType && !allowedTypes.includes(result.documentType)) {
        setErrorMessage(
          `This appears to be a ${DOCUMENT_TYPE_INFO[result.documentType]?.label || result.documentType}. ` +
          `Please scan a ${allowedTypes.map(t => DOCUMENT_TYPE_INFO[t]?.label || t).join(' or ')}.`
        );
        setPhase('ERROR');
        return;
      }

      setExtractionResult(result);
      setPhase('REVIEW');
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to extract PII from image');
      setPhase('ERROR');
    }
  }, [extractPii, subjectId, documentId, allowedTypes]);

  const handleConfirm = useCallback(async () => {
    if (!capturedBlob || !extractionResult) return;

    setPhase('EXTRACTING');

    try {
      // Now store in vault with confirmation
      const finalResult = await extractPii(capturedBlob, {
        subjectId,
        documentId,
        storeInVault: true,
      });

      // Build token map
      const tokens: Record<string, string> = {};
      for (const field of finalResult.extractedFields) {
        if (field.token) {
          tokens[field.entityType] = field.token;
        }
      }

      setPhase('CONFIRMED');

      onComplete?.({
        tokens,
        documentType: finalResult.documentType,
      });
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to store PII');
      setPhase('ERROR');
    }
  }, [capturedBlob, extractionResult, extractPii, subjectId, documentId, onComplete]);

  const handleRetake = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setCapturedBlob(null);
    setPreviewUrl(null);
    setExtractionResult(null);
    setErrorMessage(null);
    setPhase('CAPTURING');
  }, [previewUrl]);

  const handleCancel = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPhase('IDLE');
    onCancel?.();
  }, [previewUrl, onCancel]);

  // Render based on phase
  if (phase === 'IDLE') {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Stack align="center" gap="md">
          <ThemeIcon size={60} radius="xl" variant="light" color="blue">
            <IconCreditCard size={30} />
          </ThemeIcon>
          <Title order={4}>Scan ID Card</Title>
          <Text c="dimmed" ta="center" size="sm">
            Take a photo of an SSN card, driver&apos;s license, or insurance card.
            PII is extracted locally and stored securely.
          </Text>
          <Group>
            <Button
              leftSection={<IconCamera size={18} />}
              onClick={() => setPhase('CAPTURING')}
            >
              Open Camera
            </Button>
            {onCancel && (
              <Button variant="subtle" onClick={handleCancel}>
                Cancel
              </Button>
            )}
          </Group>
        </Stack>
      </Card>
    );
  }

  if (phase === 'CAPTURING') {
    return (
      <LivePhotoCapture
        onUploadComplete={(results) => {
          if (results[0]?.blob) {
            handlePhotoCapture(results[0].blob);
          }
        }}
        onError={(err) => {
          setErrorMessage(err.message);
          setPhase('ERROR');
        }}
        sourceSystem="pii-card-scanner"
      />
    );
  }

  if (phase === 'EXTRACTING' || isExtracting) {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text>Extracting information from card...</Text>
          <Text size="sm" c="dimmed">
            OCR and PII detection running locally
          </Text>
        </Stack>
      </Card>
    );
  }

  if (phase === 'REVIEW' && extractionResult) {
    const docInfo = extractionResult.documentType
      ? DOCUMENT_TYPE_INFO[extractionResult.documentType]
      : null;

    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="md">
          <Group justify="space-between">
            <Title order={4}>Review Extracted Data</Title>
            {docInfo && (
              <Badge color={docInfo.color} size="lg">
                {docInfo.label}
              </Badge>
            )}
          </Group>

          {previewUrl && (
            <Paper withBorder p="xs" radius="md" style={{ maxHeight: 150, overflow: 'hidden' }}>
              <img
                src={previewUrl}
                alt="Captured card"
                style={{ width: '100%', objectFit: 'contain', maxHeight: 140 }}
              />
            </Paper>
          )}

          <Alert
            icon={<IconShieldCheck size={16} />}
            color="green"
            variant="light"
          >
            This data will be encrypted and stored in a secure vault.
            Only tokens will be saved in the main system.
          </Alert>

          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Field</Table.Th>
                <Table.Th>Value</Table.Th>
                <Table.Th>Confidence</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {extractionResult.extractedFields.map((field, idx) => (
                <Table.Tr key={idx}>
                  <Table.Td>
                    <Text fw={500}>
                      {ENTITY_TYPE_LABELS[field.entityType] || field.entityType}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text ff="monospace">{maskForReview(field)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      color={field.confidence > 0.9 ? 'green' : field.confidence > 0.7 ? 'yellow' : 'red'}
                      variant="light"
                    >
                      {Math.round(field.confidence * 100)}%
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          {extractionResult.extractedFields.length === 0 && (
            <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
              No PII detected in image. Try taking a clearer photo.
            </Alert>
          )}

          <Group justify="flex-end">
            <Button
              variant="subtle"
              leftSection={<IconRefresh size={16} />}
              onClick={handleRetake}
            >
              Retake Photo
            </Button>
            <Button
              color="red"
              variant="light"
              leftSection={<IconX size={16} />}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              color="green"
              leftSection={<IconCheck size={16} />}
              onClick={handleConfirm}
              disabled={extractionResult.extractedFields.length === 0}
            >
              Confirm & Store
            </Button>
          </Group>
        </Stack>
      </Card>
    );
  }

  if (phase === 'CONFIRMED') {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Stack align="center" gap="md">
          <ThemeIcon size={60} radius="xl" color="green">
            <IconCheck size={30} />
          </ThemeIcon>
          <Title order={4}>Stored Securely</Title>
          <Text c="dimmed" ta="center">
            PII has been encrypted and stored in the vault.
            Tokens have been returned for the main system.
          </Text>
        </Stack>
      </Card>
    );
  }

  if (phase === 'ERROR') {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="md">
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
          >
            {errorMessage || 'An error occurred'}
          </Alert>
          <Group justify="flex-end">
            <Button variant="light" onClick={handleRetake}>
              Try Again
            </Button>
            <Button variant="subtle" onClick={handleCancel}>
              Cancel
            </Button>
          </Group>
        </Stack>
      </Card>
    );
  }

  return null;
};

/**
 * Mask sensitive values for review display.
 * Shows last 4 for SSN/DL, full for less sensitive data.
 */
function maskForReview(field: ExtractedPiiField): string {
  const { entityType, value } = field;

  if (entityType === 'US_SSN') {
    // Show as ***-**-XXXX
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 4) {
      return `***-**-${cleaned.slice(-4)}`;
    }
  }

  if (entityType === 'US_DRIVER_LICENSE') {
    // Show first 2 + last 4
    if (value.length >= 6) {
      return `${value.slice(0, 2)}${'*'.repeat(value.length - 6)}${value.slice(-4)}`;
    }
  }

  if (entityType === 'CREDIT_CARD') {
    // Show last 4 only
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 4) {
      return `****-****-****-${cleaned.slice(-4)}`;
    }
  }

  // For less sensitive data (names, addresses), show full value for review
  return value;
}

export default PiiCardScanner;
